import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";
import type { PaginatedMeta } from "./audience_helper";
import type { Campaign } from "./campaigns_helper";
import type { Template } from "./templates_helper";
import type {
  PlatformTemplateCategory,
  PlatformTemplateDescription,
} from "./super_admin_platform_templates_helper";

const apiBase = config.api.API_URL;

export type PlatformTemplateSeasonStatus = "active" | "upcoming" | "ended";

export type PlatformTemplateTag =
  | "ecommerce"
  | "educacao"
  | "saas"
  | "moda"
  | "servicos"
  | "publisher"
  | "sazonal_natal"
  | "sazonal_maes"
  | "sazonal_black_friday"
  | string;

/** Tags exibidas como chips na galeria (ordem de UX). */
export const PLATFORM_TEMPLATE_GALLERY_TAGS: PlatformTemplateTag[] = [
  "ecommerce",
  "educacao",
  "saas",
  "publisher",
  "moda",
  "servicos",
  "sazonal_natal",
  "sazonal_maes",
  "sazonal_black_friday",
  "sazonal_carnaval",
  "sazonal_dia_cliente",
  "sazonal_cyber",
];

export type PlatformTemplate = {
  id: number;
  name: string;
  category: PlatformTemplateCategory | string;
  tags?: PlatformTemplateTag[];
  variant_group?: string | null;
  variant_label?: string | null;
  description: PlatformTemplateDescription | null;
  html_body: string;
  editor_design: Record<string, unknown> | null;
  thumbnail_url?: string | null;
  is_active: boolean;
  sort_order: number;
  clone_count?: number;
  season_starts_on?: string | null;
  season_ends_on?: string | null;
  season_status?: PlatformTemplateSeasonStatus | null;
  is_favorite?: boolean | null;
  created_at?: string;
  updated_at?: string;
};

type ListResponse = ApiSuccess<PlatformTemplate[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

export type CloneAs = "template" | "campaign";

export type ClonePlatformTemplatePayload = {
  as?: CloneAs;
  name?: string;
  subject?: string;
};

export type PlatformTemplatesListParams = {
  page?: number;
  per_page?: number;
  category?: string;
  search?: string;
  tag?: string;
  sort?: "default" | "popular";
  in_season?: boolean;
  favorites?: boolean;
};

/**
 * Catálogo oficial read-only + clone (DOC 06 § 6.3 · LAC-024).
 * Usa base /api/v1 do tenant — nunca saUrl.
 */
export const getPlatformTemplates = (
  params?: PlatformTemplatesListParams
): Promise<ListResponse> => {
  return axios.get(apiBase + "/platform-templates", {
    params: {
      page: params?.page,
      per_page: params?.per_page,
      category: params?.category || undefined,
      search: params?.search || undefined,
      tag: params?.tag || undefined,
      sort: params?.sort === "popular" ? "popular" : undefined,
      in_season: params?.in_season ? 1 : undefined,
      favorites: params?.favorites ? 1 : undefined,
    },
  });
};

export const clonePlatformTemplate = (
  id: number,
  payload?: ClonePlatformTemplatePayload
): Promise<ApiSuccess<Template | Campaign>> => {
  return axios.post(
    apiBase + "/platform-templates/" + id + "/clone",
    payload || {}
  );
};

export const favoritePlatformTemplate = (
  id: number
): Promise<ApiSuccess<{ is_favorite: boolean }>> => {
  return axios.post(apiBase + "/platform-templates/" + id + "/favorite");
};

export const unfavoritePlatformTemplate = (
  id: number
): Promise<ApiSuccess<{ is_favorite: boolean }>> => {
  return axios.delete(apiBase + "/platform-templates/" + id + "/favorite");
};

/** Escolhe texto da description JSON conforme locale do painel. */
export const localizePlatformDescription = (
  description: PlatformTemplateDescription | null | undefined,
  language: string
): string => {
  if (!description || typeof description !== "object") {
    return "";
  }
  const normalized = language.replace("-", "_");
  const candidates = [
    normalized,
    language,
    language.startsWith("pt") ? "pt_BR" : "",
    language.startsWith("en") ? "en" : "",
    language.startsWith("es") ? "es" : "",
    "pt_BR",
    "en",
    "es",
  ].filter(Boolean);

  for (const key of candidates) {
    const value = description[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  const first = Object.values(description).find(
    (v) => typeof v === "string" && v.trim()
  );
  return typeof first === "string" ? first.trim() : "";
};
